import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material";
import { useConfirm } from "material-ui-confirm";
import api, { serializeSearchParams } from "../../utils/api";
import { showErrorSnackbar, showSuccessSnackbar } from "../../utils/feedback";
import { apiUrls } from "../../utils/urls";

function memberLabel(member) {
  return member?.name || member?.email || "Unknown user";
}

export default function MissingCoachingChats({
  students,
  streamUserId,
  onCreated,
}) {
  const confirm = useConfirm();
  const [creatingId, setCreatingId] = useState(null);

  if (!students?.length) {
    return null;
  }

  async function handleCreate(student) {
    setCreatingId(student.uuid);
    try {
      const previewResponse = await api.get(apiUrls.admin.studentChats(student.uuid), {
        params: serializeSearchParams({
          search_params: { chat_type: "coaching" },
        }),
      });
      const previewData = previewResponse?.data?.data || {};
      if (!previewData.can_create) {
        const members = previewData.members || [];
        const viewerIsMember = members.some(
          (member) => String(member.id) === String(streamUserId)
        );
        if (previewData.exists && members.length && !viewerIsMember) {
          showSuccessSnackbar(
            `Coaching chat already exists for ${members.map(memberLabel).join(", ")}. You are not a member, so it will not appear in this inbox.`
          );
        } else if (previewData.exists && viewerIsMember) {
          showSuccessSnackbar("Coaching chat already exists.");
        } else {
          showErrorSnackbar(
            previewData.reason || "Unable to create this coaching chat."
          );
        }
        onCreated();
        return;
      }

      let didConfirm = false;
      await confirm({
        title: `Create coaching chat for ${student.name}`,
        content: (
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              This chat will include:
            </Typography>
            <List dense sx={{ py: 0 }}>
              {(previewData.members || []).map((member) => (
                <ListItem key={member.id} sx={{ py: 0.25 }}>
                  <Typography variant="body2">
                    {memberLabel(member)}
                    {member.email ? ` (${member.email})` : ""}
                  </Typography>
                </ListItem>
              ))}
            </List>
          </Box>
        ),
        confirmationText: "Create Chat",
        cancellationText: "Cancel",
      })
        .then(() => {
          didConfirm = true;
        })
        .catch(() => {
          didConfirm = false;
        });

      if (!didConfirm) {
        return;
      }

      const response = await api.post(apiUrls.admin.studentChats(student.uuid), {
        chat_type: "coaching",
      });
      if (response?.data?.success === false) {
        showErrorSnackbar(
          response?.data?.reason || "Failed to create coaching chat."
        );
        return;
      }

      const payload = response?.data?.data || {};
      const members = payload.members || [];
      const viewerIsMember = members.some(
        (member) => String(member.id) === String(streamUserId)
      );
      if (viewerIsMember) {
        showSuccessSnackbar(
          payload.created
            ? "Coaching chat created."
            : "Coaching chat already exists."
        );
      } else {
        const names = members.map(memberLabel).join(", ");
        showSuccessSnackbar(
          `Coaching chat ${payload.created ? "created" : "already exists"} for ${names}. You are not a member, so it will not appear in this inbox.`
        );
      }
      onCreated();
    } catch (error) {
      showErrorSnackbar(
        error?.response?.data?.reason ||
          error?.response?.data?.error ||
          "Failed to create coaching chat."
      );
    } finally {
      setCreatingId(null);
    }
  }

  return (
    <Box
      sx={{
        borderBottom: 1,
        borderColor: "divider",
        maxHeight: 220,
        overflow: "auto",
        flexShrink: 0,
      }}
    >
      <Typography variant="caption" sx={{ display: "block", px: 1.5, pt: 1 }}>
        No coaching chat ({students.length})
      </Typography>
      <List dense disablePadding>
        {students.map((student) => {
          const cohortName = (student.cohort_name || "").replace(" Start Date", "");
          const secondary = [student.email, cohortName].filter(Boolean).join(" · ");
          const isCreating = creatingId === student.uuid;
          return (
            <ListItem
              key={student.uuid}
              secondaryAction={
                <Button
                  size="small"
                  disabled={Boolean(creatingId)}
                  onClick={() => handleCreate(student)}
                >
                  {isCreating ? <CircularProgress size={16} /> : "Create"}
                </Button>
              }
              sx={{ pr: 10 }}
            >
              <ListItemText
                primary={student.name}
                secondary={secondary}
                primaryTypographyProps={{ noWrap: true, variant: "body2" }}
                secondaryTypographyProps={{ noWrap: true }}
              />
            </ListItem>
          );
        })}
      </List>
    </Box>
  );
}
